/**
 * lib/useTeachingCycle.ts
 * ------------------------------------------------------------------
 * The teaching cycle with everything it depends on, live: the school's
 * settings (`reportSchoolSettings/teachingCycle`), HR's public holidays and
 * the term dates (School Settings → Term Dates). The rule itself is
 * lib/teachingCycle.ts; this only gathers its inputs, so the settings screen
 * and the timetable screens can never disagree about what day it is.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { usePublicHolidays } from '@/lib/publicHolidays';
import { isInTerm } from '@/lib/termDates';
import { setCycleResolver } from '@/lib/timetable';
import {
  DEFAULT_CYCLE, TEACHING_CYCLE_DOC, cycleDayOf, gaboroneToday,
  type CycleContext, type TeachingCycleSettings,
} from '@/lib/teachingCycle';

export interface TeachingCycleState {
  settings: TeachingCycleSettings;
  ctx: CycleContext;
  ready: boolean;
  /** Today's cycle day, or null (no teaching today, or not set up). */
  today: number | null;
  dayOf: (iso: string) => number | null;
}

export function useTeachingCycle(): TeachingCycleState {
  const { termCalendar } = useData();
  const { holidayName } = usePublicHolidays();
  const [settings, setSettings] = useState<TeachingCycleSettings>(DEFAULT_CYCLE);
  const [ready, setReady] = useState(false);

  useEffect(() => onSnapshot(
    doc(db, COLLECTIONS.schoolSettings, TEACHING_CYCLE_DOC),
    (s) => { setSettings(s.exists() ? { ...DEFAULT_CYCLE, ...(s.data() as Partial<TeachingCycleSettings>) } : DEFAULT_CYCLE); setReady(true); },
    () => setReady(true),
  ), []);

  const ctx = useMemo<CycleContext>(() => ({
    holidayName,
    inTerm: (iso) => isInTerm(iso, termCalendar),
  }), [holidayName, termCalendar]);

  return useMemo(() => {
    const dayOf = (iso: string) => cycleDayOf(iso, settings, ctx);
    /* Registered as it is computed, so the timetable libraries (which cannot
       use hooks) answer from the same settings in this very render. */
    setCycleResolver(dayOf);
    return { settings, ctx, ready, today: dayOf(gaboroneToday()), dayOf };
  }, [settings, ctx, ready]);
}
